/**
 * Pre-order flag for items added outside the product form.
 *
 * The hidden input in snippets/preorder-line-property.liquid only exists on the product
 * page. Collection quick add, the Boost/USF search grid, the pre-order app buttons and
 * upsell widgets post straight to /cart/add.js with nothing but an id and a quantity, so
 * those lines arrive with no property at all.
 *
 * Rather than patching every one of those call sites, this reconciles the cart after the
 * fact: find lines missing the flag, ask the theme whether their variant is pre-order,
 * and attach the property to the ones that are. That covers any add path, including ones
 * made by apps we do not control.
 *
 * The pre-order test lives in Liquid (snippets/preorder-variant-map.liquid, served via
 * /products/<handle>?view=preorderjson) because Shopify's own /products/<handle>.js omits
 * inventory_quantity, which the rule depends on.
 *
 * This lives in its own file, deliberately. It used to sit at the bottom of custom.js,
 * where any error earlier in that file would stop it ever registering — and custom.js
 * runs plenty of unrelated DOM code that depends on elements being present. Nothing here
 * needs jQuery or the theme, so it has no reason to share their fate.
 *
 * To check it by hand from the console: window.preorderCartSync()
 */
(function () {
  "use strict";

  var LABEL = "Pre-Order"; // must match prop_label in preorder-line-property.liquid
  var VALUE = "will be available for pickup in 7-10 working days";

  var PICKUP_LABEL = "Available"; // must match prop_label in pickup-availability-property.liquid
  var PICKUP_VALUE = "pickup in 24 hours";

  var mapCache = {}; // handle -> Promise<{variantId: bool}>
  var running = false;

  function preorderMap(handle) {
    if (!mapCache[handle]) {
      // Accept MUST NOT be application/json. Shopify inspects this header on a product
      // URL and, when it says JSON, answers with its own product payload and ignores
      // ?view= entirely — so the alternate template never runs and every lookup silently
      // misses. Asking for html gets the template; the body is parsed by hand below.
      mapCache[handle] = fetch("/products/" + encodeURIComponent(handle) + "?view=preorderjson", {
        headers: { Accept: "text/html" },
        credentials: "same-origin"
      })
        .then(function (r) { return r.ok ? r.text() : ""; })
        .then(function (text) {
          try {
            return JSON.parse(text.trim());
          } catch (e) {
            if (window.console) console.warn("[preorder] bad response for " + handle, text.slice(0, 120));
            return {};
          }
        })
        .catch(function () { return {}; }); // a lookup failure must never block the cart
    }
    return mapCache[handle];
  }

  function reconcile() {
    if (running) return Promise.resolve(false);
    running = true;

    return fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Presence, not truthiness: the value is a space, which is falsy. Testing
        // the value would mark every already-flagged line as still pending, and on the cart
        // page that means reconcile -> reload -> reconcile, forever.
        var pending = (cart.items || []).filter(function (item) {
          return !(item.properties && (Object.prototype.hasOwnProperty.call(item.properties, LABEL) || Object.prototype.hasOwnProperty.call(item.properties, PICKUP_LABEL)));
        });
        if (!pending.length) return false;

        return Promise.all(pending.map(function (item) { return preorderMap(item.handle); }))
          .then(function (maps) {
            var preorderChanges = pending.filter(function (item, i) {
              return maps[i] && maps[i][item.variant_id] === true;
            });
            var availableChanges = pending.filter(function (item, i) {
              return !(maps[i] && maps[i][item.variant_id] === true);
            });

            if (!preorderChanges.length && !availableChanges.length) return false;

            var allChanges = preorderChanges.concat(availableChanges);

            // One at a time: concurrent cart mutations overwrite each other, and every
            // change rewrites the line keys of the lines that follow it.
            return allChanges.reduce(function (chain, item) {
              return chain.then(function () {
                var props = {};
                for (var k in (item.properties || {})) props[k] = item.properties[k];

                var isPreorder = preorderChanges.indexOf(item) !== -1;
                if (isPreorder) {
                  props[LABEL] = VALUE;
                } else {
                  props[PICKUP_LABEL] = PICKUP_VALUE;
                }

                return fetch("/cart/change.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Accept: "application/json" },
                  credentials: "same-origin",
                  body: JSON.stringify({ id: item.key, properties: props })
                });
              });
            }, Promise.resolve()).then(function () { return true; });
          });
      })
      .catch(function () { return false; })
      .then(function (changed) { running = false; return changed; });
  }

  // On the cart page the markup is already rendered, so a change needs a repaint. This
  // cannot loop: the second pass finds the property present and changes nothing.
  function reconcileAndRefresh() {
    return reconcile().then(function (changed) {
      if (changed && /(^|\/)cart(\/|$)/.test(window.location.pathname)) {
        window.location.reload();
      } else if (changed) {
        // Writing a property gives the line a new key. The mini cart was rendered with the old
        // key, so its remove button would send a dead id ("no valid id or line parameter").
        // Ask the theme to re-render the drawer so the buttons carry the current keys.
        document.dispatchEvent(new Event("cart:refresh"));
      }
      return changed;
    });
  }

  window.preorderCartSync = reconcileAndRefresh;

  function isCartAdd(url) {
    return typeof url === "string" && url.indexOf("/cart/add") !== -1;
  }

  // Catch adds however they are made — apps use fetch or XHR, and we own neither.
  var nativeFetch = window.fetch;
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = (typeof input === "string") ? input : (input && input.url);
      var result = nativeFetch.apply(this, arguments);
      if (isCartAdd(url)) result.then(function () { reconcileAndRefresh(); }, function () {});
      return result;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartAdd(url)) {
      this.addEventListener("load", function () { reconcileAndRefresh(); });
    }
    return nativeOpen.apply(this, arguments);
  };

  // Safety net for every path the hooks miss — an app that captured fetch before us, a
  // full-page form post, or lines added before this shipped. Landing on any page repairs
  // the cart, which is why the cart page heals itself even if the interception fails.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { reconcileAndRefresh(); });
  } else {
    reconcileAndRefresh();
  }
})();
